import { RecentlyViewedChangedEvent } from '@theme/events';

/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   * @param {number} [restoreIndex] - When restoring (not a genuine new view), the index to reinsert
   * the product at instead of moving it to the front. Used to undo a removal back into its original spot.
   */
  static addProduct(productId, restoreIndex) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);

    if (typeof restoreIndex === 'number' && restoreIndex >= 0 && restoreIndex <= viewedProducts.length) {
      viewedProducts.splice(restoreIndex, 0, productId);
    } else {
      viewedProducts.unshift(productId);
    }

    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    document.dispatchEvent(new RecentlyViewedChangedEvent(productId, true));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Removes a single product from the recently viewed products list.
   * @param {string} productId - The ID of the product to remove.
   */
  static removeProduct(productId) {
    const viewedProducts = this.getProducts().filter((/** @type {string} */ id) => id !== productId);
    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    document.dispatchEvent(new RecentlyViewedChangedEvent(productId, false));
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}
